import { useMemo } from 'react'
import { useAtom } from 'jotai'
import { useResetAtom } from 'jotai/utils'
import lodash from 'lodash'
const { isEqual } = lodash
import { viewStateStore } from '../store'
import {
  useViewStateActiveAtom,
  useViewStateDefaultAtom,
  useViewStatePreferredDefaultAtom,
} from './atoms'
import { useViewStateScope } from './view-state-scope'
import type { Scope } from '../types'

/**
 * Exposes funcs that allow you to interact with the current view state.
 */
export const useViewState = ({
  scope: requestedScope,
}: { scope?: Scope } = {}) => {
  const scope = useViewStateScope(requestedScope)
  const viewStateAtom = useViewStateActiveAtom(scope)
  const [viewState, setViewState] = useAtom(viewStateAtom, {
    store: viewStateStore,
  })
  const [preferredDefaultId, setPreferredDefaultId] = useAtom(
    useViewStatePreferredDefaultAtom(scope),
    { store: viewStateStore },
  )
  const resetViewState = useResetAtom(viewStateAtom)
  const [originalViewState] = useAtom(useViewStateDefaultAtom(scope), {
    store: viewStateStore,
  })
  const viewStateHasChanged = useMemo(
    () => !isEqual(viewState, originalViewState),
    [viewState, originalViewState],
  )

  return {
    viewState,
    setViewState,
    resetViewState,
    originalViewState,
    viewStateHasChanged,
    preferredDefaultId,
    setPreferredDefaultId,
  }
}
